import { useEffect, useRef, useState } from 'react';
import { Wordmark } from './Wordmark';
import { DashboardIcon } from './DashboardIcon';
import { WorkspacePattern } from './WorkspacePattern';
import './landing-draft.css';

const appUrl = 'https://app.myalphaquant.com';

const features = [
  {
    id: 'command',
    index: '01',
    icon: 'grid',
    label: 'Command Centre',
    title: 'Your trading, at a glance.',
    body: 'An overview of your whole platform in one place: net P&L, win rate, streaks, open positions and pinned notes. It’s where you land every time you sign in.',
    points: [],
    image: '/images/my-alpha/command.jpg',
    alt: 'Command Centre with net P&L, win rate, and the day’s overview',
  },
  {
    id: 'flow',
    index: '02',
    icon: 'flow',
    label: 'Daily Flow',
    title: 'The day, on one desk.',
    body: 'Every trading day runs in four steps: Pre-market Setup, Discipline, Trade recap and Day wrap. Recap each trade, grade the day and sign it off.',
    points: [
      'Mood and pre-market checks before the open',
      'Goal sets and task lists the day is held to',
      'Recap your trades once the session is done',
      'Sign off to keep the day as a record',
    ],
    image: '/images/my-alpha/flow.jpg',
    alt: 'Daily Flow with pre-market setup, discipline, trade recap, and day wrap',
  },
  {
    id: 'session',
    index: '03',
    icon: 'clock',
    label: 'Session Log',
    title: 'Every trading day, ready to view.',
    body: 'One card per trading day, with its net P&L, trades, reviews and win rate. Open a session to replay each trade, link its strategy and keep the note on the trade.',
    points: [
      'Realized P&L drawn for the day',
      'A Trading Calendar you can expand, zoom out and share',
      'Compare trades side by side',
    ],
    image: '/images/my-alpha/session.jpg',
    alt: 'Session Log with a card for each trading day',
  },
  {
    id: 'analytics',
    index: '04',
    icon: 'chart',
    label: 'Analytics',
    title: 'Every number, explained.',
    body: 'Includes a myAlpha trading score, net P&L, win rate and profit factor up front. Each tab answers one question, and every number has an ⓘ that explains it.',
    points: [
      'Your myAlpha score, thoughtfully constructed to reflect your trading',
      'Time, risk, drawdown and behaviour, each on their own tab',
      'Comparisons and custom charts in your own workspace',
    ],
    image: '/images/my-alpha/analytics.jpg',
    alt: 'Analytics with the myAlpha score, net P&L, win rate, and profit factor',
  },
  {
    id: 'clario',
    index: '05',
    icon: 'spark',
    label: 'Clario',
    title: 'A little clarity for your next move.',
    body: 'Clario, the Alpha AI assistant, will review your desk with you and suggest a useful next step. Coming soon.',
    points: [],
    image: '/images/my-alpha/clario.jpg',
    alt: 'Clario, the Alpha AI assistant, ready for a review',
  },
  {
    id: 'playbooks',
    index: '06',
    icon: 'book',
    label: 'Playbooks',
    title: 'Your rules, written down once.',
    body: 'Strategies, discipline rules, tags and every note you write, in one library. Daily Flow and the Session Log use it, so the plan you wrote is the plan you review.',
    points: [
      'Strategies with trade management rules and performance summaries',
      'Task lists you tick, and goal sets scored automatically from your trades',
      'Active discipline sets land on every new day',
    ],
    image: '/images/my-alpha/playbooks.jpg',
    alt: 'Playbooks library with strategies, discipline rules, tags, and notes',
  },
];

const faqs = [
  {
    q: 'What is MyAlpha?',
    a: 'A trading journal and analytics app. It imports your trades automatically, walks you through each trading day, and shows you what your numbers really say.',
  },
  {
    q: 'Who is it for?',
    a: 'Traders on Alpha Futures, Alpha Capital and Alpha Prime accounts who want their journal, rules and stats in one place.',
  },
  {
    q: 'Do I have to enter my trades by hand?',
    a: 'No. Connect your account once and every trade comes in automatically. You add the parts only you know: your mood, your notes and how well you followed your plan.',
  },
  {
    q: 'Can MyAlpha place trades or touch my money?',
    a: 'No. MyAlpha only reads your trades. It can’t place, change or close a trade, and it can’t move money.',
  },
  {
    q: 'What’s inside?',
    a: 'Command Centre for your day at a glance, Daily Flow to plan and close each trading day, Session Log to review every trade, Analytics for your stats, and Playbooks for your strategies, rules and notes.',
  },
  {
    q: 'Can I connect more than one account?',
    a: 'Yes. Connect as many accounts as you trade, across Alpha Futures, Alpha Capital and Alpha Prime. See them all together, or switch to one account in a click.',
  },
  {
    q: 'Does it work with copy trading?',
    a: 'Yes. Pick your lead account and the follower accounts that copy it. You can then judge the performance of just the lead, or of the whole group together.',
  },
  {
    q: 'I passed an evaluation and moved to a funded account. Can I see it as one journey?',
    a: 'Yes. Link the accounts in the order you traded them and MyAlpha shows your stats across the whole journey, from evaluation to funded.',
  },
  {
    q: 'What is Clario?',
    a: 'Clario is MyAlpha’s AI assistant. It will review your journal with you, keep your rules close and suggest a useful next step. It’s coming soon.',
  },
];

function AlphaMark() {
  return (
    <svg viewBox="0 0 21.48 22.72" width="13" height="14" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.1268 11.4764 13.9578 17.048 10.7507 11.4764 7.96975 16.2717 4.81385 16.2622 10.7551 5.77172 13.9516 11.3426 15.5383 8.52397 10.7507 0 0 19.0998h9.56428l1.17502-1.9686 3.1837 5.588 3.2205-5.5711L18.285 19.0998h3.1939L17.1268 11.4764Z"
      />
    </svg>
  );
}

function TabIcon({ name }) {
  return (
    <span className="desk-tab-icon">
      <DashboardIcon name={name} />
    </span>
  );
}

function LaunchLink({ className }) {
  return (
    <a className={className} href={appUrl}>
      <AlphaMark />
      Launch My Alpha
    </a>
  );
}

function FaqList() {
  const [open, setOpen] = useState([0]);
  return (
    <div className="faq-list">
      {faqs.map((item, index) => {
        const expanded = open.includes(index);
        const panelId = `faq-panel-${index}`;
        const buttonId = `faq-button-${index}`;
        return (
          <div className={expanded ? 'faq-item is-open' : 'faq-item'} key={item.q}>
            <h3>
              <button
                type="button"
                id={buttonId}
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() =>
                  setOpen((current) =>
                    current.includes(index) ? current.filter((itemIndex) => itemIndex !== index) : [...current, index],
                  )
                }
              >
                <span>{item.q}</span>
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M5 7.5 10 12.5 15 7.5" />
                </svg>
              </button>
            </h3>
            <div id={panelId} role="region" aria-labelledby={buttonId} className="faq-panel">
              <div>
                <p>{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LandingDraftPage() {
  const root = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(() => {
    const id = window.location.hash.replace('#', '');
    return features.some((feature) => feature.id === id) ? id : features[0].id;
  });
  const current = features.find((feature) => feature.id === active) ?? features[0];

  // The shell scrolls its main column, not the window, and a scroll event
  // doesn't bubble, so listen in the capture phase and measure the page itself.
  useEffect(() => {
    const onScroll = () => setScrolled((root.current?.getBoundingClientRect().top ?? 0) < -8);
    onScroll();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  return (
    <div className="site landing-draft ma-system" data-ma-theme="dark" ref={root}>
      <div className="site-workspace" aria-hidden="true">
        <WorkspacePattern />
      </div>
      <a className="site-skip" href="#desk">
        Skip to the desk
      </a>
      <header className={scrolled ? 'site-header is-scrolled' : 'site-header'}>
        <a className="site-logo" href="#top" aria-label="MyAlpha home">
          <Wordmark tone="light" />
        </a>
        <div className="site-header-actions">
          <LaunchLink className="site-button site-button-primary launch-btn" />
        </div>
      </header>

      <main id="top">
        <section className="hero is-flat" aria-labelledby="hero-title">
          <div className="hero-stage">
            <div className="hero-copy">
              <h1 id="hero-title">
                Free trading journal and
                <br />
                backtesting tool for <span>Alpha traders.</span>
              </h1>
              <p>
                MyAlpha is the trading journal and analytics desk for every trader. Your trades import automatically, so
                your time goes on the review.
              </p>
              <div className="hero-actions">
                <LaunchLink className="site-button site-button-primary launch-btn" />
              </div>
            </div>
            <div className="hero-card">
              <img src="/images/my-alpha/command.jpg" alt="MyAlpha Command Centre on the sample desk" width={2880} height={1800} />
            </div>
          </div>
        </section>

        <section className="desk" id="desk" aria-labelledby="desk-title">
          <div className="desk-intro">
            <h2 id="desk-title">One desk for the whole session.</h2>
          </div>
          <div className="desk-board">
            <div className="desk-tabs" role="tablist" aria-label="The desk">
              {features.map((feature) => (
                <button
                  type="button"
                  role="tab"
                  id={`desk-tab-${feature.id}`}
                  key={feature.id}
                  className={active === feature.id ? 'desk-tab is-on' : 'desk-tab'}
                  aria-selected={active === feature.id}
                  aria-controls="desk-panel"
                  onClick={() => setActive(feature.id)}
                >
                  <TabIcon name={feature.icon} />
                  <span>{feature.label}</span>
                  {feature.id === 'clario' && <em className="soon-tag">Soon</em>}
                </button>
              ))}
            </div>
            <article
              className="desk-panel is-in"
              id="desk-panel"
              role="tabpanel"
              aria-labelledby={`desk-tab-${current.id}`}
            >
              <div className="desk-copy" key={current.id}>
                <p className="site-kicker">
                  {current.index} / {current.label}
                </p>
                <h3>{current.title}</h3>
                <p>{current.body}</p>
                {current.points.length > 0 && (
                  <ul>
                    {current.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                )}
                <LaunchLink className="desk-open" />
              </div>
              <figure className="desk-shot">
                {features.map((feature) => (
                  <img
                    key={feature.id}
                    className={active === feature.id ? 'is-on' : undefined}
                    src={feature.image}
                    alt={active === feature.id ? feature.alt : ''}
                    width={2880}
                    height={1800}
                    aria-hidden={active !== feature.id}
                  />
                ))}
              </figure>
            </article>
          </div>
        </section>

        <section className="faq" id="faq" aria-labelledby="faq-title">
          <h2 id="faq-title">
            <span>Frequently Asked Questions</span>
            <span> Everything you need to know about MyAlpha.</span>
          </h2>
          <FaqList />
        </section>

        <section className="close" aria-labelledby="close-title">
          <p>From traders on the desk</p>
          <h2 id="close-title">
            Stay disciplined.
            <br />
            Stay accountable.
          </h2>
          <div className="hero-actions">
            <LaunchLink className="site-button site-button-primary launch-btn" />
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-hero">
          <div className="footer-word" aria-hidden="true">
            <Wordmark fade />
          </div>
          <div className="footer-rule" aria-hidden="true" />
        </div>
      </footer>
    </div>
  );
}
